import { IAuthMeV2OutputDto } from '@quadminds/shared/auth-system';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import useGePermissions from './useGetAllPermissions';
import { FileItem } from '../types/File/File.types';

const useMapPermissions = (file: FileItem | null) => {
    const queryClient = useQueryClient();
    const { getAllPermissionsQuery, getPermissionsPerDocument } = useGePermissions(false, file?.id);
    const permissionIds = useMemo(() => {
        return {
            admin: getAllPermissionsQuery.data?.find((p) => p.code === 'admin')?.id ?? '',
            editor: getAllPermissionsQuery.data?.find((p) => p.code === 'editor')?.id ?? '',
            viewer: getAllPermissionsQuery.data?.find((p) => p.code === 'viewer')?.id ?? '',
        };
    }, [getAllPermissionsQuery.data]);

    const userInfo: IAuthMeV2OutputDto | undefined = queryClient.getQueryData(['userInfo']);

    const userPermission = useMemo(() => {
        return getPermissionsPerDocument.data?.find((perm) => perm.user_id === userInfo?.id);
    }, [getPermissionsPerDocument.data, userInfo?.id]);

    const isAdmin = useMemo(() => {
        return file?.uploaded_by?.userId === userInfo?.id || userPermission?.permissions.includes(permissionIds.admin);
    }, [file?.uploaded_by?.userId, userInfo?.id, userPermission, permissionIds.admin]);

    const isEditor = useMemo(() => {
        return userPermission?.permissions.includes(permissionIds.editor);
    }, [userPermission, permissionIds.editor]);

    const isViewer = useMemo(() => {
        return userPermission?.permissions.includes(permissionIds.viewer);
    }, [userPermission, permissionIds.viewer]);

    return {
        userPermission,
        isAdmin,
        isEditor,
        isViewer,
        userInfo,
    };
};

export default useMapPermissions;
